/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { IconDownload, IconPlus, IconDotsVertical } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import DropdownFilter from "../../../components/common/DropdownFilter";
import ListView from "../../../components/common/ListView";
import NormalSearch from "../../../components/common/NormalSearch";
import ToggleButton from "../../../components/ToggleButton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import { useFetchNotificationsData } from "../../../apis/notificationApis";

const NotificationMange: React.FC = () => {
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit] = useState<number>(10);
  const debouncedSearch = useDebounce(searchFor, 550);

  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useFetchNotificationsData(pageNo, limit, debouncedSearch);

  const notifications = data?.notifications || [];
  const total = data?.total || 0;
  const totalPages = total ? Math.ceil(total / limit) : 1;

  useEffect(() => {
    setPageNo(1);
  }, [debouncedSearch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="rounded-3xl">
        <div className="flex items-start">
          <div className=" bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[30px] ">
            <h1 className="text-3xl text-[#212121]">Notification Draft</h1>
            <div className="flex items-center gap-2">
              <ColumnsFilter columnsData={[]} />
              <DropdownFilter filterheadings={[]} />
              <NormalSearch
                value={searchFor}
                onChange={(e) => {
                  setSearchFor(e.target.value);
                  setPageNo(1);
                }}
              />
            </div>
          </div>
          <div className="relative ml-5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={40}
              height={40}
              viewBox="0 0 34 34"
              fill="none"
              className="absolute -bottom-[12.5px] -left-[22.5px] rotate-[2deg] "
            >
              <path
                d="M0.5 0.5C4.94768 14.4784 15.2221 25.8511 28.6786 31.6907L34 34H2.31268L0.5 0.5Z"
                fill="#F7F6F2"
              />
            </svg>

            <div className=" flex items-center gap-2 justify-end rounded-full">
              <button className="flex items-center gap-1  p-3 text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2]">
                <IconDownload stroke={1.5} size={24} />
              </button>
              <button className="flex items-center gap-1  p-3 text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2]">
                <IconPlus stroke={1.5} size={24} />
              </button>
            </div>
          </div>
        </div>

        <div>
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading notifications...</div>
          ) : (
            <ListView
              data={notifications}
              onRowClick={(row: any) => console.log("Clicked:", row)}
              columns={[
                { label: "#", render: (_: any, i: number) => (pageNo - 1) * limit + i + 1 },
                {
                  label: "Notification ID",
                  render: (c: any) => (
                    <span className="font-medium">{c.id ? `#${c.id.slice(0, 8).toUpperCase()}` : "—"}</span>
                  ),
                },
                {
                  label: "Title",
                  render: (c: any) => (
                    <span className="font-medium">{c.title || "—"}</span>
                  ),
                },
                { label: "Created By", render: (c: any) => c.createdBy || "—" },
                {
                  label: "Recipients",
                  render: (c: any) => {
                    const users = c.users || [];
                    if (!users.length) return "—";
                    return (
                      <span className="text-sm text-gray-600">
                        {users.length} recipient{users.length !== 1 ? "s" : ""}
                      </span>
                    );
                  },
                },
                {
                  label: "Sender Type",
                  className: "text-center",
                  render: (c: any) => {
                    const typeColors: Record<string, string> = {
                      SMS: "bg-[#EEEEEE] text-[#212121]",
                      Email: "bg-[#EEEEEE] text-[#212121]",
                      Whatsapp: "bg-[#EBF7CE] text-[#212121]",
                      Banner: "bg-[#FFF3CD] text-[#212121]",
                    };

                    const typesArray: string[] = Array.isArray(c.types)
                      ? c.types
                      : typeof c.types === "string"
                      ? [c.types]
                      : [];

                    if (!typesArray.length) return <span className="text-gray-400">—</span>;

                    return (
                      <div className="flex gap-1 justify-center flex-wrap">
                        {typesArray.map((t: string, index: number) => (
                          <span
                            key={index}
                            className={`rounded-full w-fit px-3 py-0.5 text-sm text-center ${
                              typeColors[t] || "bg-gray-100"
                            }`}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    );
                  },
                },
                {
                  label: "Status",
                  render: (c: any) => <ToggleButton checked={c.status === "ACTIVE"} />,
                },
                {
                  label: "Actions",
                  render: (c: any) => (
                    <div className="relative">
                      <button
                        onClick={() =>
                          setOpenDropdownIndex(openDropdownIndex === c.id ? null : c.id)
                        }
                        className="p-2 border rounded-full bg-[#F7F6F2]"
                      >
                        <IconDotsVertical />
                      </button>

                      {openDropdownIndex === c.id && (
                        <div
                          ref={dropdownRef}
                          className="absolute bg-[#F7F6F2] divide-y divide-[#979797]/50 right-28 -top-2 z-50 mt-2 w-32 rounded-xl shadow-lg overflow-hidden"
                        >
                          <div className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer">
                            View
                          </div>
                          <div className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer">
                            Edit
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>

        <div className="flex justify-center items-center py-4">
          {totalPages > 1 && (
            <Pagination
              currentPage={pageNo}
              totalPages={totalPages}
              fetchData={(page) => setPageNo(page)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationMange;
